import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Space, message } from 'antd';
import { UserOutlined, LockOutlined, ShopOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { registerSupplier } from '../services/authService';
import { Supplier } from '../models/Supplier';
import { getApiErrorMessage } from '../utils/errorMessages';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const Register: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const supplier = new Supplier();
      supplier.email = values.email;
      supplier.password = values.password;
      supplier.supplier_name = values.supplier_name;
      supplier.first_name = values.first_name;
      supplier.last_name = values.last_name;

      await registerSupplier(supplier);
      message.success(t('registerSuccess'));
      navigate('/home');
    } catch (error: any) {
      if (error?.profileCreationFailed) {
        message.error(t('registerProfileError'));
      } else {
        message.error(getApiErrorMessage(error, 'registerError', t));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '20px',
      background: 'linear-gradient(135deg, #334E3B 0%, #C8CBC0 100%)'
    }}>
      <div style={{ position: 'fixed', top: 16, right: 16 }}>
        <LanguageSwitcher />
      </div>
      <Card style={{ width: 500, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
        <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
          <Title level={2}>{t('register')}</Title>
          <Text type="secondary">{t('registerSubtitle')}</Text>
          
          <Form
            name="register"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
          >
            <Form.Item
              name="supplier_name"
              label={t('supplierName')}
              rules={[{ required: true, message: t('supplierNameRequired') }]}
            >
              <Input
                prefix={<ShopOutlined />}
                placeholder={t('supplierName')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="first_name"
              label={t('firstName')}
              rules={[{ required: true, message: t('firstNameRequired') }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={t('firstName')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="last_name"
              label={t('lastName')}
              rules={[{ required: true, message: t('lastNameRequired') }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={t('lastName')}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="email"
              label={t('email')}
              rules={[
                { required: true, message: t('emailRequired') },
                { type: 'email', message: t('emailInvalid') }
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder={t('email')}
                size="large"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={t('password')}
              rules={[
                { required: true, message: t('passwordRequired') },
                { min: 6, message: t('passwordMinLength') }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('password')}
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirm_password"
              label={t('confirmPassword')}
              dependencies={['password']}
              rules={[
                { required: true, message: t('confirmPasswordRequired') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('passwordsMismatch')));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('confirmPassword')}
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
              >
                {t('register')}
              </Button>
            </Form.Item>
          </Form>

          <Text>
            {t('alreadyHaveAccount')} <Link to="/login">{t('loginHere')}</Link>
          </Text>
        </Space>
      </Card>
    </div>
  );
};

export default Register;
